// React
import { useEffect, useMemo } from "react"

// Router
import {
	useLocation,
	useNavigationType,
	createRoutesFromChildren,
	matchRoutes,
	createBrowserRouter,
} from "@/lib/router"

// Sentry
import * as Sentry from "@sentry/react"

// Network error constants
import { NetworkError } from "@/api/rest/network-errors"

// Browser error constants
import { BrowserError } from "./browser-errors"

// Env variables
import {
	VERCEL_ENV,
	SENTRY_DSN,
	SENTRY_DEBUG,
	SENTRY_ENABLED,
	VERCEL_GIT_COMMIT_SHA,
} from "../env"

// Ignore these errors
const IGNORE_ERRORS = [
	// Network errors
	NetworkError.NETWORK_ERROR,
	NetworkError.FAILED_TO_FETCH,
	NetworkError.NETWORK_ERROR_RESOURCE,
	// Browser extension errors (not from our application)
	BrowserError.RUNTIME_SEND_MESSAGE,
	BrowserError.TAB_NOT_FOUND,
	BrowserError.EXTENSION_CONTEXT_INVALIDATED,
	BrowserError.CHROME_RUNTIME,
	BrowserError.BROWSER_RUNTIME,
	BrowserError.EXTENSION_ID_ERROR,
	BrowserError.CONTENT_SCRIPT_ERROR,
	BrowserError.INVALID_EXTENSION_ID,
	BrowserError.SAFELINKS_CRAWLER,
	BrowserError.DUCKDUCKGO_FEATURE,
	BrowserError.WKWEBVIEW_TARGET_FRAME,
	BrowserError.ZARGET_SCRIPT,
]

// Sensitive GraphQL variable fields to mask
const SENSITIVE_GRAPHQL_FIELDS = [
	"password",
	"current_password",
	"token",
	"accessToken",
	"refreshToken",
	"authToken",
	"iban",
]

/**
 * Recursively masks sensitive fields in GraphQL variables
 * @param obj - The object containing GraphQL variables
 * @returns A new object with sensitive fields masked
 */
export const maskSensitiveGraphQLVariables = (obj: any): any => {
	if (obj === null || obj === undefined) {
		return obj
	}

	if (Array.isArray(obj)) {
		return obj.map(maskSensitiveGraphQLVariables)
	}

	if (typeof obj === "object") {
		const masked: any = {}
		for (const [key, value] of Object.entries(obj)) {
			const lowerKey = key.toLowerCase()
			// Check if the field name contains any sensitive keyword
			const isSensitive = SENSITIVE_GRAPHQL_FIELDS.some((field) =>
				lowerKey.includes(field.toLowerCase()),
			)

			if (isSensitive && value !== null && value !== undefined) {
				// Mask the value but preserve the type indication
				if (typeof value === "string") {
					masked[key] = "[REDACTED]"
				} else if (typeof value === "number") {
					masked[key] = 0
				} else if (typeof value === "boolean") {
					masked[key] = false
				} else {
					masked[key] = "[REDACTED]"
				}
			} else if (typeof value === "object") {
				// Recursively mask nested objects
				masked[key] = maskSensitiveGraphQLVariables(value)
			} else {
				masked[key] = value
			}
		}
		return masked
	}

	return obj
}

// Init Sentry
export const initSentry = () => {
	Sentry.init({
		dsn: SENTRY_DSN,
		debug: SENTRY_DEBUG === "true",
		enabled: SENTRY_ENABLED === "true",
		environment: VERCEL_ENV,

		// Sentry replay rates are set to 0 because we manually start and stop the replay
		replaysSessionSampleRate: 0,
		replaysOnErrorSampleRate: 0,

		// Extra integrations
		integrations: [
			/**
			 * Performance Tracing
			 * This also tracks all core web-vitals (https://docs.sentry.io/product/performance/web-vitals/)
			 * DOCS: https://docs.sentry.io/platforms/javascript/guides/react/performance/
			 * DOCS: https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
			 */
			Sentry.reactRouterV7BrowserTracingIntegration({
				useEffect,
				useLocation,
				useNavigationType,
				createRoutesFromChildren,
				matchRoutes,
			}),

			/**
			 * Record session replay videos
			 * DOCS: https://docs.sentry.io/platforms/javascript/session-replay/
			 */
			Sentry.replayIntegration({
				maskAllText: false,
				maskAllInputs: true,
				blockAllMedia: false,
			}),
		],

		/**
		 * Ensure trace propagation to our backend domain
		 */
		tracePropagationTargets: ["zonhub.com", "app.zonhub.com"],

		/**
		 * Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
		 * We recommend adjusting this value in production
		 */
		tracesSampleRate: VERCEL_ENV === "production" ? 0.2 : 1.0,

		// Release info
		release: VERCEL_GIT_COMMIT_SHA,

		// Beforesend hook
		beforeSend: async (event) => {
			// Ignore errors
			try {
				for (const error of event?.exception?.values ?? []) {
					for (const matcher of IGNORE_ERRORS) {
						if (error.value?.match(matcher)) {
							return null
						}
					}
				}
			} catch {}

			// Mask sensitive data in event extras if present
			if (event.extra) {
				event.extra = maskSensitiveGraphQLVariables(event.extra)
			}

			// Mask sensitive data in contexts if present
			if (event.contexts) {
				event.contexts = maskSensitiveGraphQLVariables(event.contexts)
			}

			return event
		},

		// BeforeBreadcrumb hook to mask sensitive data in breadcrumbs
		beforeBreadcrumb: (breadcrumb) => {
			// Mask GraphQL variables in breadcrumbs
			if (
				breadcrumb.category === "graphql" &&
				breadcrumb.data?.variables
			) {
				breadcrumb.data.variables = maskSensitiveGraphQLVariables(
					breadcrumb.data.variables,
				)
			}

			// Also mask any data object that might contain sensitive info
			if (breadcrumb.data) {
				breadcrumb.data = maskSensitiveGraphQLVariables(breadcrumb.data)
			}

			return breadcrumb
		},
	})
}

/**
 * SentryReplayCapture
 *
 * This is a component that allows you to start and stop Sentry replay capture.
 * It is used to capture replays for the investor page.
 * @returns
 */
export function SentryReplayCapture() {
	const location = useLocation()

	// Memoize the investor path check
	const isInvestorPath = useMemo(() => {
		return location.pathname.startsWith("/investor")
	}, [location.pathname])

	// This will run whenever the location changes
	useEffect(() => {
		// Start Sentry replay capture
		// Listen to navigation events for when users land on our buggy URL, appropriately named: "/buggy-page/"
		// and then start recording a replay.
		if (isInvestorPath) {
			startSentryReplayCapture()
		} else {
			void sendSentryReplayCapture()
		}
	}, [isInvestorPath])

	// Return nothing
	return null
}

// Start Sentry replay capture
const startSentryReplayCapture = () => {
	if (SENTRY_ENABLED !== "true") {
		console.warn("Sentry is not enabled. Cannot start replay capture.")
		return
	}

	const replay = Sentry.getReplay()
	if (!replay) {
		console.warn(
			"Sentry replay is not available. Make sure Sentry is properly initialized.",
		)
		return
	}

	try {
		/**
		 * Start replay buffering.
		 * Sentry replays can either be in 'session' or 'buffering' mode.
		 *
		 * `buffer`: Always keeping the last 60s of recording, requires:
		 *   - having replaysOnErrorSampleRate > 0 to capture replay when an error occurs
		 *   - or calling `flush()` to send the replay
		 *
		 * !! DO NOT CALL `replay.start()` HERE.
		 */
		replay.startBuffering()
		console.log("Sentry replay started successfully")
	} catch (error) {
		console.error("Failed to start Sentry replay:", error)
	}
}

// Send replay to Sentry
const sendSentryReplayCapture = async () => {
	if (SENTRY_ENABLED !== "true") {
		console.warn("Sentry is not enabled. Cannot send replay capture.")
		return
	}

	// Get the replay instance
	const replay = Sentry.getReplay()

	/**
	 * If we successfully started buffering, we can send the replay
	 */
	if (replay?.getRecordingMode() === "buffer") {
		try {
			/**
			 * When calling `flush()`, Sentry will send the last 60 seconds of recording from buffer to the Sentry server.
			 *
			 * !! IMPORTANT !!
			 * Only call `flush()` if you are in "buffer" recording mode.
			 * Otherwise, it will fall back to the following behaviour:
			 *
			 * If not in "session" recording mode, flush event buffer which will create a new replay.
			 * If replay is not enabled, a new session replay is started.
			 * Unless `continueRecording` is false, the replay will continue to record and
			 * behave as a "session"-based replay.
			 * Otherwise, queue up a flush.
			 */
			await replay.flush()
			console.log("Sentry replay sent successfully")
		} catch (error) {
			console.error("Failed to send Sentry replay:", error)
		}
	} else {
		console.warn(
			"Sentry replay is not available. Make sure Sentry is properly initialized.",
		)
		return
	}
}

/**
 * Use these classnames to block, ignore or mask certain DOM elements from Sentry Session Replay
 * DOCS: https://docs.sentry.io/platforms/javascript/guides/remix/session-replay/privacy/#masking
 */
export type SentryReplayPrivacyClassNames =
	| "sentry-block"
	| "sentry-ignore"
	| "sentry-mask"

// Set user
export const setSentryUser = ({
	id,
	name,
	email,
}: {
	id: string
	name: string
	email: string
}) => {
	Sentry.setUser({
		id,
		name,
		email,
	})
}

// Capture exception
export const captureException = (
	...args: Parameters<typeof Sentry.captureException>
) => {
	try {
		return Sentry.captureException(...args)
	} catch (e) {
		console.error("Failed to log exception", e)
	}
}

// Add breadcrumb
export const addBreadcrumb = (args: Sentry.Breadcrumb) => {
	try {
		return Sentry.addBreadcrumb({
			...args,
			level: "info",
		})
	} catch (e) {
		console.error("Failed to set breadcrumb", e)
	}
}

// captureMessage
export const captureMessage = (
	...args: Parameters<typeof Sentry.captureMessage>
) => {
	try {
		return Sentry.captureMessage(...args)
	} catch (e) {
		console.error("Failed to capture message", e)
	}
}

// Error Boundary
export const ErrorBoundary = Sentry.ErrorBoundary
export type { ErrorBoundaryProps } from "@sentry/react"

/**
 * Instrument 'Routes' with Sentry Tracing
 *
 * DOCS: https://docs.sentry.io/platforms/javascript/guides/react/features/react-router/v7/
 */
export const sentryCreateBrowserRouter: typeof createBrowserRouter =
	Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)
